/**
 * IntegrityTab — the product-instance integrity surface (§STEP 6).
 *
 * PRIMARY VIEW = THE LADDER.
 *   Columns = `stage` (the order work actually flows: core_hr → timekeeping →
 *   payroll → general_ledger). Rows = `lane` (which vendor stack).
 *   FORWARD seams are horizontal RAILS. LATERAL seams (same stage, different
 *   lane) are vertical RUNGS = "the two stacks disagree". SELF seams are node
 *   BADGES, never self-loops.
 *   The read is one line: rails leak forward, rungs mean the stacks disagree.
 *
 * DRILL-DOWN = THE TRIAGE BOARD. Click any rail / rung / badge to pin its card.
 *
 * ENCODING CONSTRAINT (measured, not stylistic): severity CANNOT be normalized
 * across seams — the seams carry 6 distinct units, counts run 0→394, only some
 * carry a population and only one carries USD. `state` is the ONLY cross-seam
 * comparable dimension, so COLOUR ENCODES STATE, FULL STOP. Count + unit render
 * as text. No edge-thickness-by-count, no heat-by-count — that would assert that
 * 394 deductions outrank $713,400 across 2 GL lines.
 *
 * This component is a PURE RENDERER. `stage` / `lane` / `kind` are all derived
 * server-side (routers/backbone.py, integrity2) and consumed as given. There is
 * no domain knowledge in this file — no hardcoded vendor list, no hardcoded
 * stage order, no hardcoded lane assignment. A single-stack tenant collapses to
 * one lane with no rungs by construction.
 */
import { useState, useEffect, useMemo, useRef } from 'react'
import { getIntegrity2 } from '../../api'
import './IntegrityTab.css'

// ─── Pure display maps (labels only — no logic) ───────────────────────────────
const FN_LABEL = {
  core_hr: 'Core HR',
  timekeeping: 'Timekeeping',
  payroll: 'Payroll',
  general_ledger: 'General ledger',
  benefits: 'Benefits',
}
const STATE_LABEL = { gap: 'Gap', degraded: 'Degraded', resolves: 'Resolves' }
const STATE_ORDER = ['gap', 'degraded', 'resolves']
const KIND_LABEL = { forward: 'Rail — forward hop', lateral: 'Rung — the stacks disagree', self: 'Badge — inside one product' }

const prettyProduct = (id) => String(id || '').replace(/_/g, ' ')
const fnLabel = (fn) => FN_LABEL[fn] || String(fn || '').replace(/_/g, ' ')

// ─── Geometry ─────────────────────────────────────────────────────────────────
const NODE_W = 158
const NODE_H = 58
const COL_W = 244
const LANE_H = 156
const LEFT = 132       // room for lane labels
const TOP = 92         // room for column headers

export default function IntegrityTab({ customerId }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selected, setSelected] = useState(null)   // seam id
  const cardRefs = useRef({})

  useEffect(() => {
    let dead = false
    setLoading(true); setError(null); setSelected(null)
    getIntegrity2(customerId)
      .then(d => { if (!dead) { setData(d); setLoading(false) } })
      .catch(e => { if (!dead) { setError(e?.message || 'Failed to load integrity surface'); setLoading(false) } })
    return () => { dead = true }
  }, [customerId])

  // ── LAYOUT: derived entirely from the payload's stage / lane / kind ─────────
  const layout = useMemo(() => {
    if (!data || !Array.isArray(data.products) || !data.products.length) return null
    const products = data.products
    const seams = Array.isArray(data.seams) ? data.seams : []

    // Columns = distinct stages, in flow order.
    const stages = [...new Set(products.map(p => p.stage))].sort((a, b) => a - b)
    const stageIdx = Object.fromEntries(stages.map((s, i) => [s, i]))

    // Rows = distinct real lanes. 'shared' centres between them; 'rail' sits below.
    const laneKeys = [...new Set(products.map(p => p.lane))]
      .filter(l => l !== 'shared' && l !== 'rail')
      .sort()
    const hasShared = products.some(p => p.lane === 'shared')
    const hasRail = products.some(p => p.lane === 'rail')

    const laneY = {}
    laneKeys.forEach((l, i) => { laneY[l] = TOP + i * LANE_H })
    const firstY = laneKeys.length ? laneY[laneKeys[0]] : TOP
    const lastY = laneKeys.length ? laneY[laneKeys[laneKeys.length - 1]] : TOP
    const sharedY = (firstY + lastY) / 2
    const railY = lastY + LANE_H

    const yOf = (lane) => lane === 'shared' ? sharedY : lane === 'rail' ? railY : (laneY[lane] ?? TOP)
    const xOf = (stage) => LEFT + (stageIdx[stage] ?? 0) * COL_W

    // Node positions. If two products land in the same cell they stack — generic,
    // never hit by a 2-lane tenant, but a 3-payroll tenant must not overlap.
    const cell = {}
    const pos = {}
    products.forEach(p => {
      const k = `${p.stage}|${p.lane}`
      const n = (cell[k] = (cell[k] || 0) + 1)
      pos[p.id] = { x: xOf(p.stage), y: yOf(p.lane) + (n - 1) * (NODE_H + 16), p }
    })

    // Lane labels: the vendor of the core_hr instance seeding that lane.
    const minStage = stages[0]
    const laneLabel = {}
    laneKeys.forEach(l => {
      const seed = products.find(p => p.lane === l && p.stage === minStage)
      laneLabel[l] = seed ? seed.vendor : l.toUpperCase()
    })

    // Column headers: the fn of the products actually sitting in the lanes.
    const stageLabel = stages.map(s => {
      const inStage = products.filter(p => p.stage === s)
      const primary = inStage.find(p => p.lane !== 'rail') || inStage[0]
      return { stage: s, x: xOf(s), label: fnLabel(primary?.fn) }
    })

    // ── Edges ────────────────────────────────────────────────────────────────
    const rails = []    // forward
    const rungs = []    // lateral
    const badges = {}   // self, keyed by product

    seams.forEach(s => {
      const f = pos[s.from_product], t = pos[s.to_product]
      if (s.kind === 'self') {
        (badges[s.from_product] = badges[s.from_product] || []).push(s)
        return
      }
      if (!f || !t) return                       // seam references a product we can't place
      if (s.kind === 'lateral') { rungs.push(s); return }
      rails.push(s)
    })

    // Rails: right edge → left edge, cubic. Midpoint of that cubic is exactly the
    // midpoint of the endpoints, which is where the count label goes.
    const railGeom = rails.map(s => {
      const f = pos[s.from_product], t = pos[s.to_product]
      const sx = f.x + NODE_W / 2, sy = f.y
      const tx = t.x - NODE_W / 2, ty = t.y
      const dx = Math.max(34, (tx - sx) / 2)
      return {
        seam: s,
        d: `M ${sx} ${sy} C ${sx + dx} ${sy}, ${tx - dx} ${ty}, ${tx} ${ty}`,
        lx: (sx + tx) / 2,
        ly: (sy + ty) / 2,
      }
    })

    // Rungs: vertical, in-column. Antiparallel pairs (A→B and B→A at the same
    // stage — real: identity_orphan vs identity_rekey) must not be drawn on top
    // of each other, so a group of n fans out symmetrically about the column.
    const groups = {}
    rungs.forEach(s => {
      const key = `${pos[s.from_product].x}|${[s.from_product, s.to_product].sort().join('~')}`
      ;(groups[key] = groups[key] || []).push(s)
    })
    const rungGeom = []
    Object.values(groups).forEach(g => {
      const n = g.length
      g.forEach((s, i) => {
        const off = (i - (n - 1) / 2) * 40
        const f = pos[s.from_product], t = pos[s.to_product]
        const down = t.y > f.y
        const x = f.x + off
        const y1 = f.y + (down ? NODE_H / 2 : -NODE_H / 2)
        const y2 = t.y + (down ? -NODE_H / 2 : NODE_H / 2)
        rungGeom.push({
          seam: s,
          d: `M ${x} ${y1} L ${x} ${y2}`,
          lx: x + (off >= 0 ? 12 : -12),
          ly: (y1 + y2) / 2,
          anchor: off >= 0 ? 'start' : 'end',
        })
      })
    })

    const width = LEFT + (stages.length - 1) * COL_W + NODE_W / 2 + 60
    const height = (hasRail ? railY : lastY) + NODE_H / 2 + 56

    return {
      products, pos, laneKeys, laneLabel, laneY, stageLabel,
      sharedY, railY, hasShared, hasRail,
      railGeom, rungGeom, badges, width, height,
    }
  }, [data])

  const selectSeam = (id) => {
    setSelected(id)
    const el = cardRefs.current[id]
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  if (loading) return <div className="it-msg">Deriving the integrity surface…</div>
  if (error) return <div className="it-msg it-msg-error">{error}</div>
  if (!data) return null
  if (!layout) return <div className="it-msg">No product instances found for this tenant — nothing to seam.</div>

  const seams = data.seams || []
  const vitals = data.vitals || {}
  const weakestSeam = seams.find(s => s.id === data.weakest)

  return (
    <div className="it-wrap">

      {/* ═══ VITALS ═══ */}
      <div className="it-vitals">
        <div className={`it-chain it-${data.chain_integrity}`}>
          <div className="it-chain-label">Chain integrity</div>
          <div className="it-chain-value">{STATE_LABEL[data.chain_integrity] || data.chain_integrity}</div>
          {weakestSeam && <div className="it-chain-sub">Weakest hop — {weakestSeam.name}</div>}
        </div>
        <div className="it-vital-row">
          <button className={`it-vital gap ${selected === null ? '' : ''}`} onClick={() => setSelected(null)}>
            <div className="it-vital-value">{vitals.gaps ?? 0}</div>
            <div className="it-vital-label">Gaps</div>
          </button>
          <button className="it-vital degraded" onClick={() => setSelected(null)}>
            <div className="it-vital-value">{vitals.degraded ?? 0}</div>
            <div className="it-vital-label">Degraded</div>
          </button>
          <button className="it-vital resolves" onClick={() => setSelected(null)}>
            <div className="it-vital-value">{vitals.resolves ?? 0}</div>
            <div className="it-vital-label">Resolves</div>
          </button>
          <div className="it-vital total">
            <div className="it-vital-value">{vitals.total ?? seams.length}</div>
            <div className="it-vital-label">Seams</div>
          </div>
        </div>
      </div>

      {/* ═══ THE LADDER ═══ */}
      <div className="it-ladder-card">
        <div className="it-ladder-head">
          <div>
            <h3>Integrity ladder</h3>
            <div className="it-ladder-sub">
              Rails leak forward. Rungs mean the stacks disagree.
              {' '}<span className="it-dim">Colour is state — counts are not comparable across seams, so they render as text.</span>
            </div>
          </div>
          <div className="it-legend">
            <span className="it-legend-item"><i className="it-swatch gap" /> Gap</span>
            <span className="it-legend-item"><i className="it-swatch degraded" /> Degraded</span>
            <span className="it-legend-item"><i className="it-swatch resolves" /> Resolves</span>
          </div>
        </div>

        <div className="it-ladder-scroll">
          <svg
            className="it-svg"
            viewBox={`0 0 ${layout.width} ${layout.height}`}
            width={layout.width}
            height={layout.height}
            role="img"
            aria-label="Product-instance integrity ladder"
          >
            <defs>
              {STATE_ORDER.map(st => (
                <marker key={st} id={`it-arrow-${st}`} viewBox="0 0 10 10" refX="9" refY="5"
                        markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                  <path className={`it-arrow ${st}`} d="M 0 0 L 10 5 L 0 10 z" />
                </marker>
              ))}
            </defs>

            {/* column headers */}
            {layout.stageLabel.map(s => (
              <text key={s.stage} className="it-col-head" x={s.x} y={44} textAnchor="middle">
                {s.label}
              </text>
            ))}

            {/* lane labels + baselines */}
            {layout.laneKeys.map(l => (
              <g key={l}>
                <text className="it-lane-label" x={LEFT - NODE_W / 2 - 18} y={layout.laneY[l] + 4} textAnchor="end">
                  {layout.laneLabel[l]}
                </text>
                <line className="it-lane-rule" x1={LEFT - NODE_W / 2 - 8} y1={layout.laneY[l]}
                      x2={layout.width - 30} y2={layout.laneY[l]} />
              </g>
            ))}
            {layout.hasShared && (
              <text className="it-lane-label it-lane-shared" x={LEFT - NODE_W / 2 - 18} y={layout.sharedY + 4} textAnchor="end">
                shared
              </text>
            )}
            {layout.hasRail && (
              <text className="it-lane-label it-lane-shared" x={LEFT - NODE_W / 2 - 18} y={layout.railY + 4} textAnchor="end">
                side rail
              </text>
            )}

            {/* RAILS — forward hops */}
            {layout.railGeom.map(({ seam, d, lx, ly }) => {
              const dim = selected && selected !== seam.id
              return (
                <g key={seam.id} className={`it-edge-g ${dim ? 'dim' : ''}`} onClick={() => selectSeam(seam.id)}>
                  <path className="it-edge-hit" d={d} />
                  <path className={`it-edge ${seam.state} ${selected === seam.id ? 'sel' : ''}`} d={d}
                        markerEnd={`url(#it-arrow-${seam.state})`} />
                  <EdgeLabel seam={seam} x={lx} y={ly} anchor="middle" />
                </g>
              )
            })}

            {/* RUNGS — the stacks disagree */}
            {layout.rungGeom.map(({ seam, d, lx, ly, anchor }) => {
              const dim = selected && selected !== seam.id
              return (
                <g key={seam.id} className={`it-edge-g ${dim ? 'dim' : ''}`} onClick={() => selectSeam(seam.id)}>
                  <path className="it-edge-hit" d={d} />
                  <path className={`it-edge rung ${seam.state} ${selected === seam.id ? 'sel' : ''}`} d={d}
                        markerEnd={`url(#it-arrow-${seam.state})`} />
                  <EdgeLabel seam={seam} x={lx} y={ly} anchor={anchor} />
                </g>
              )
            })}

            {/* NODES — product instances */}
            {layout.products.map(p => {
              const { x, y } = layout.pos[p.id]
              const selfSeams = layout.badges[p.id] || []
              return (
                <g key={p.id} className="it-node-g">
                  <rect className="it-node" x={x - NODE_W / 2} y={y - NODE_H / 2}
                        width={NODE_W} height={NODE_H} rx="8" />
                  <text className="it-node-name" x={x} y={y - 4} textAnchor="middle">{prettyProduct(p.id)}</text>
                  <text className="it-node-fn" x={x} y={y + 14} textAnchor="middle">{fnLabel(p.fn)}</text>

                  {/* SELF seams = badges on the node, never self-loops */}
                  {selfSeams.map((s, i) => {
                    const bx = x + NODE_W / 2 - 12 - i * 24
                    const by = y - NODE_H / 2
                    const dim = selected && selected !== s.id
                    return (
                      <g key={s.id} className={`it-badge-g ${dim ? 'dim' : ''}`} onClick={() => selectSeam(s.id)}>
                        <circle className={`it-badge ${s.state} ${selected === s.id ? 'sel' : ''}`}
                                cx={bx} cy={by} r="9" />
                        <text className="it-badge-text" x={bx} y={by + 3.5} textAnchor="middle">
                          {s.state === 'resolves' ? '✓' : '!'}
                        </text>
                        <title>{s.name} — {s.count} {s.unit}</title>
                      </g>
                    )
                  })}
                </g>
              )
            })}
          </svg>
        </div>
      </div>

      {/* ═══ TRIAGE BOARD — the drill-down ═══ */}
      <div className="it-triage-head">
        <h3>Triage</h3>
        {selected
          ? <button className="it-clear" onClick={() => setSelected(null)}>Show all {seams.length} seams</button>
          : <span className="it-dim">Click any rail, rung, or badge above to pin it.</span>}
      </div>

      <div className="it-board">
        {STATE_ORDER.map(st => {
          const col = seams.filter(s => s.state === st)
          return (
            <div key={st} className={`it-col ${st}`}>
              <div className="it-col-head-bar">
                <span className={`it-swatch ${st}`} />
                {STATE_LABEL[st]}
                <span className="it-col-count">{col.length}</span>
              </div>
              {col.length === 0 && <div className="it-col-empty">None</div>}
              {col.map(s => (
                <SeamCard
                  key={s.id}
                  seam={s}
                  selected={selected === s.id}
                  dim={selected && selected !== s.id}
                  onClick={() => setSelected(selected === s.id ? null : s.id)}
                  innerRef={el => { cardRefs.current[s.id] = el }}
                />
              ))}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Edge label: count + unit as TEXT. Never thickness, never heat. ───────────
function EdgeLabel({ seam, x, y, anchor }) {
  const clean = seam.count === 0
  const txt = clean ? 'clean' : `${Number(seam.count).toLocaleString()} ${seam.unit}`
  const w = Math.max(46, txt.length * 6.6 + 12)
  const rx = anchor === 'middle' ? x - w / 2 : anchor === 'start' ? x : x - w
  return (
    <g className="it-edge-label">
      <rect x={rx} y={y - 10} width={w} height={20} rx="5" className="it-edge-label-bg" />
      <text x={anchor === 'middle' ? x : anchor === 'start' ? x + 6 : x - 6} y={y + 4}
            textAnchor={anchor === 'middle' ? 'middle' : anchor === 'start' ? 'start' : 'end'}
            className={`it-edge-label-text ${seam.state}`}>
        {txt}
      </text>
    </g>
  )
}

// ─── Triage card ──────────────────────────────────────────────────────────────
function SeamCard({ seam, selected, dim, onClick, innerRef }) {
  return (
    <div
      ref={innerRef}
      className={`it-card ${seam.state} ${selected ? 'sel' : ''} ${dim ? 'dim' : ''}`}
      onClick={onClick}
    >
      <div className="it-card-top">
        <span className={`it-kind ${seam.kind}`}>{seam.kind}</span>
        <span className="it-card-anchor">{seam.anchor}</span>
      </div>
      <div className="it-card-name">{seam.name}</div>
      <div className="it-card-sub">{seam.sub}</div>

      <div className="it-card-metric">
        <span className={`it-card-count ${seam.state}`}>
          {Number(seam.count || 0).toLocaleString()}
        </span>
        <span className="it-card-unit">{seam.unit}</span>
        {seam.population != null && (
          <span className="it-card-pop">of {Number(seam.population).toLocaleString()} in scope</span>
        )}
        {seam.usd != null && (
          <span className="it-card-usd">${Number(seam.usd).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        )}
      </div>

      <div className="it-card-label">{seam.label}</div>

      {selected && (
        <div className="it-card-detail">
          <div className="it-card-cost">{seam.cost}</div>
          <div className="it-card-prov-head">Provenance</div>
          <div className="it-card-prov">{seam.provenance}</div>
          <div className="it-card-flow">
            {prettyProduct(seam.from_product)} <span className="it-arrow-glyph">→</span> {prettyProduct(seam.to_product)}
            <span className="it-card-kindnote">{KIND_LABEL[seam.kind]}</span>
          </div>
        </div>
      )}
    </div>
  )
}
